import React, { useState, startTransition } from 'react';

import ProfileMenu from './ProfileMenu/ProfileMenu';
import { SupportModal } from '../../../../features/support/support';
import BoostModal from '../../../../features/boost/BoostModal';

import { Box, Button, Card, IconButton, Stack, Toolbar, Tooltip, Typography, useMediaQuery, useTheme, } from '@mui/material';

import FullLogo from '../../../../assets/images/fullLogo.png';

import MenuIcon from '@mui/icons-material/Menu';

import { useAuth } from '../../../../context/AuthContext/AuthContext';
import { isImpersonating } from '../../../../utils/impersonationSession';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';


import {
  headerCardSx,
  leftSectionSx,
  logoImgSx,
  menuButtonSx,
  metricButtonSx,
  metricIconBoxSx,
  metricIconSx,
  metricLabelSx,
  metricRowSx,
  middleSectionSx,

  rightSectionSx,
  toolbarSx,
  valueInIconBoxSx,
  valueOutsideIconBoxSx,
} from './styles';
import { Rocket, Wallet } from 'lucide-react';
import WithdrawModal from '../../../../features/withdraw/WithdrawModal';
import KYCVerificationModal from '../../../KYCVerificationModal/KYCVerificationModal';

interface HeaderProps {
  open: boolean;
  drawerWidth: number;
  handleDrawerOpen: () => void;
  bannerHeight?: number;
  isRtl?: boolean;
}

const Header: React.FC<HeaderProps> = ({
  open,
  drawerWidth,
  handleDrawerOpen,
  bannerHeight = 0,
  isRtl = false,
}) => {
  const theme = useTheme();
  const { user, profile, refreshProfile } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation(['header']);
  const navigate = useNavigate();

  // Support modal state
  const [openSupportModal, setOpenSupportModal] = useState(false);
  const handleCloseSupportModal = () => setOpenSupportModal(false);

  // Withdraw modal state
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);
  const handleCloseWithdrawModal = async (wasSuccessful?: boolean) => {
    setIsWithdrawModalOpen(false);

    if (wasSuccessful) {
      try {
        await refreshProfile();
      } catch (error) {
        console.error("Failed to refresh profile after withdrawal", error);
      }
    }
  };

  // KYC Verification modal state
  const [isKYCModalOpen, setIsKYCModalOpen] = useState(false);
  const handleCloseKYCModal = () => setIsKYCModalOpen(false);

  // Handle withdraw button click - check KYC validation first
  const handleOpenWithdrawModal = () => {
    if (profile?.isKYCValidated) {
      startTransition(() => {
        setIsWithdrawModalOpen(true);
      });
    } else {
      setIsKYCModalOpen(true);
    }
  };

  // Boost modal state
  const [openBoostModal, setOpenBoostModal] = useState(false);
  // Margin boosts only exist for instant funded accounts: the metric is shown only to users
  // who already have an active instant funded account.
  const showBoostMetric = Boolean(profile?.hasActiveInstantFunded);
  const handleCloseBoostModal = () => setOpenBoostModal(false);

  const handleBoostButtonClick = () => {
    if (!profile?.hasActiveInstantFunded) {
      toast.warning(t('TOAST.BOOST_REQUIRES_INSTANT_FUNDED'));
      navigate('/buy-funded');
    } else {
      setOpenBoostModal(true);
    }
  };


  const walletAmount = parseFloat(profile?.walletAmount ?? '0');
  const isWalletEmpty = isNaN(walletAmount) || walletAmount === 0;

  if (!user) return null;

  const isAdmin = user.role === 'ROLE_ADMIN';
  const isAdminView = isImpersonating();

  return (
    <>

      <Card sx={headerCardSx(theme, open, isMobile, drawerWidth, bannerHeight, isRtl)}>
        <Toolbar sx={toolbarSx(isRtl)}>
          {/* Left section: menu + logo */}
          <Stack {...leftSectionSx}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerOpen}
              edge="start"
              sx={menuButtonSx(open, isMobile)}
            >
              <MenuIcon />
            </IconButton>

            {!isMobile && (
              <Box component={Link} to="/">
                <Box
                  component="img"
                  sx={logoImgSx(open)}
                  alt="RaiseMyFunds logo"
                  src={FullLogo}
                />
              </Box>
            )}
          </Stack>

          {!isAdmin && (
            <Stack {...middleSectionSx}>
              {/* Wallet */}
              <Stack {...metricRowSx}>
                <Box
                  component="div"
                  sx={{ ...(metricIconBoxSx as object), ...(isMobile && { cursor: (isWalletEmpty || isAdminView) ? 'not-allowed' : 'pointer' }) }}
                  onClick={isMobile && !isWalletEmpty && !isAdminView ? handleOpenWithdrawModal : undefined}
                  aria-disabled={isMobile && (isWalletEmpty || isAdminView)}
                  role="button"
                  tabIndex={isMobile && !isWalletEmpty && !isAdminView ? 0 : -1}
                >
                  <Box component={Wallet} sx={metricIconSx} />
                  {/* MOBILE: value inside the box */}
                  <Typography sx={valueInIconBoxSx}>
                    {profile?.walletAmount
                      ? `${parseFloat(profile.walletAmount).toLocaleString('fr-FR')} €`
                      : '0 €'}
                  </Typography>
                </Box>

                <Typography sx={metricLabelSx}>{t('CURRENT_WALLET')}</Typography>

                {/* DESKTOP: value outside the box */}
                <Typography sx={valueOutsideIconBoxSx}>
                  {profile?.walletAmount
                    ? `${parseFloat(profile.walletAmount).toLocaleString('fr-FR')} €`
                    : '0 €'}
                </Typography>

                <Tooltip
                  title={isAdminView ? t('ADMIN_VIEW_TOOLTIP') : isWalletEmpty ? t('WALLET_EMPTY_TOOLTIP') : ''}
                  disableHoverListener={!isWalletEmpty && !isAdminView}
                >
                  <span>
                    <Button variant="contained" size="small" sx={metricButtonSx} onClick={handleOpenWithdrawModal} disabled={isWalletEmpty || isAdminView}>
                      {t('WITHDRAW')}
                    </Button>
                  </span>
                </Tooltip>
              </Stack>

              {/* Boost */}
              {showBoostMetric && (
              <Stack {...metricRowSx}>
                <Box
                  component="div"
                  sx={{ ...(metricIconBoxSx as object), ...(isMobile && { cursor: 'pointer' }) }}
                  onClick={isMobile ? handleBoostButtonClick : undefined}
                >
                  <Box component={Rocket} sx={metricIconSx} />
                  {/* MOBILE: value inside the box */}
                  <Typography sx={valueInIconBoxSx}>
                    {profile?.marginBoostsCount ?? 0}
                  </Typography>
                </Box>

                <Typography sx={metricLabelSx}>{t('MARGIN_BOOST')}</Typography>

                {/* DESKTOP: value outside the box */}
                <Typography sx={valueOutsideIconBoxSx}>
                  {profile?.marginBoostsCount ?? 0}
                </Typography>

                <Button
                  variant="contained"
                  size="small"
                  sx={metricButtonSx}
                  onClick={handleBoostButtonClick}
                >
                  {t('MANAGE_BOOSTS')}
                </Button>
              </Stack>
              )}
            </Stack>
          )}

          {/* Right section: promos + support + profile */}
          <Stack {...rightSectionSx}>
            <ProfileMenu />
          </Stack>
        </Toolbar>
      </Card>

      <SupportModal
        openSupportModal={openSupportModal}
        handleClose={handleCloseSupportModal}
      />

      <BoostModal
        open={openBoostModal}
        onClose={handleCloseBoostModal}
      />

      {/* Withdraw Modal */}
      <WithdrawModal
        open={isWithdrawModalOpen}
        onClose={handleCloseWithdrawModal}
      />

      {/* KYC Verification Modal */}
      <KYCVerificationModal
        open={isKYCModalOpen}
        onClose={handleCloseKYCModal}
      />
    </>
  );
};

export default Header;